import type {
  TaskFlag,
  TaskPriority,
  TaskProfile,
} from '@travel-planner/shared/schemas/tasks';
import type { CSSProperties } from 'react';
import { useAccountsForFilters } from '@auth/entities/account';
import { useListTaskCollections } from '@task-manager/entities/task-collections';
import {
  CircleAlert,
  Filter,
  Flag as FlagIcon,
  LayoutDashboard,
  LayoutTemplate,
  User,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trpc } from '@/shared/api/trpc';
import { pickCss } from '@/shared/lib/css-utils';
import { Badge, Button, MultiSelect, Select, Text } from '@/shared/ui';
import css from './tasks-filters-popover.module.scss';

const triggerStyles = pickCss(css, css.trigger);

const PRIORITY_OPTIONS: Array<{ label: string; value: TaskPriority }> = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const PROFILE_OPTIONS: Array<{ label: string; value: TaskProfile }> = [
  { value: 'consultant', label: 'Consultant' },
  { value: 'finance', label: 'Finance' },
  { value: 'operation', label: 'Operation' },
];

const FLAG_OPTIONS: Array<{ label: string; value: string }> = [
  { value: '0', label: 'Not flagged' },
  { value: '5', label: 'Attention' },
  { value: '10', label: 'Urgent' },
];

type FilterPatch = {
  priorities?: TaskPriority[];
  profiles?: TaskProfile[];
  flags?: TaskFlag[];
  itineraryIds?: string[];
  collectionIds?: string[];
  assignedAccountId?: string;
};

type TasksFiltersPopoverProps = {
  priorities: TaskPriority[];
  profiles: TaskProfile[];
  flags: TaskFlag[];
  itineraryIds: string[];
  collectionIds: string[];
  /** Current assignee selection: an account id, or 'all' for everyone. */
  assignee?: string;
  /** Hide the assignee filter when the table is scope-locked (drawer). */
  showAssignee?: boolean;
  /** Whether the assignee is off its default (counts toward the badge). */
  assigneeActive?: boolean;
  onChange: (patch: FilterPatch) => void;
  hasActive: boolean;
  onClearAll: () => void;
};

/**
 * Single Filters button that opens a portal-rendered panel of facetted
 * filters. Replaces the inline row of MultiSelects to match the legacy
 * /tasks-management/tasks UX. Selections do NOT close the panel — the
 * user can stack filters. Closes on outside click, Escape, or Done.
 *
 * Portal + viewport-clamp pattern mirrors task-priority-cell.tsx so
 * the panel escapes the table's `overflow: hidden`.
 */
export const TasksFiltersPopover = ({
  priorities,
  profiles,
  flags,
  itineraryIds,
  collectionIds,
  assignee,
  showAssignee = false,
  assigneeActive = false,
  onChange,
  hasActive,
  onClearAll,
}: TasksFiltersPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Lazy-load lookup data — only fetch when the popover has been opened
  // at least once. TanStack Query caches between opens.
  const [hasOpened, setHasOpened] = useState(false);
  const itinerariesQuery = trpc.itineraries.search.useQuery(
    { limit: 100, offset: 0 },
    { enabled: hasOpened }
  );
  const collectionsQuery = useListTaskCollections({ limit: 100, offset: 0 });
  // Touching collectionsQuery only when hasOpened keeps it from running
  // unnecessarily; using useListTaskCollections directly already caches.

  const accountsQuery = useAccountsForFilters(hasOpened && showAssignee);
  const assigneeOptions = useMemo(
    () => [
      { value: 'all', label: 'Everyone' },
      ...(accountsQuery.data ?? []).map((a) => ({
        value: a.id,
        label: a.fullName,
      })),
    ],
    [accountsQuery.data]
  );

  const itineraryOptions = useMemo(
    () =>
      (itinerariesQuery.data?.data ?? []).map((it) => ({
        value: it.id,
        label: it.title ?? it.itineraryRef ?? it.id,
      })),
    [itinerariesQuery.data]
  );

  const collectionOptions = useMemo(
    () =>
      (collectionsQuery.data?.data ?? []).map((c) => ({
        value: c.id,
        label: c.title,
      })),
    [collectionsQuery.data]
  );

  const activeCount =
    priorities.length +
    profiles.length +
    flags.length +
    itineraryIds.length +
    collectionIds.length +
    (assigneeActive ? 1 : 0);

  useLayoutEffect(() => {
    if (!open) return;
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const viewportPadding = 8;
    const panelWidth = 320;
    const wouldOverflowRight =
      rect.left + panelWidth + viewportPadding > window.innerWidth;
    const left = wouldOverflowRight
      ? Math.max(viewportPadding, rect.right - panelWidth)
      : rect.left;
    setOffset({
      left,
      top: rect.bottom + 6,
      width: panelWidth,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-tasks-filters-popover]')) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleToggle = () => {
    setOpen((p) => !p);
    if (!hasOpened) setHasOpened(true);
  };

  return (
    <>
      <button
        aria-expanded={open}
        aria-haspopup="dialog"
        className={triggerStyles({ isOpen: open })}
        onClick={handleToggle}
        ref={triggerRef}
        type="button"
      >
        <Filter className={css.triggerIcon} />
        <span>Filters</span>
        {activeCount > 0 ? <Badge variant="active">{activeCount}</Badge> : null}
      </button>
      {open && offset
        ? createPortal(
            <div
              className={css.panel}
              data-tasks-filters-popover
              style={offset}
            >
              <div className={css.header}>
                <Text size="sm" weight="medium">
                  Filters
                </Text>
                {hasActive ? (
                  <button
                    className={css.clearLink}
                    onClick={onClearAll}
                    type="button"
                  >
                    Clear all
                  </button>
                ) : null}
              </div>
              <div className={css.fields}>
                {showAssignee ? (
                  <div className={css.field}>
                    <Text className={css.fieldLabel} size="xs" variant="muted">
                      Assignee
                    </Text>
                    <Select
                      onChange={(e) =>
                        onChange({ assignedAccountId: e.target.value })
                      }
                      options={assigneeOptions}
                      value={assignee ?? 'all'}
                    />
                  </div>
                ) : null}

                <div className={css.field}>
                  <Text className={css.fieldLabel} size="xs" variant="muted">
                    Priority
                  </Text>
                  <MultiSelect
                    leadingIcon={<CircleAlert size={14} />}
                    onChange={(values) =>
                      onChange({
                        priorities:
                          values.length > 0
                            ? (values as TaskPriority[])
                            : undefined,
                      })
                    }
                    options={PRIORITY_OPTIONS}
                    placeholder="Priority"
                    searchPlaceholder="Priority"
                    searchable={false}
                    showSelectAll={false}
                    values={priorities}
                  />
                </div>

                <div className={css.field}>
                  <Text className={css.fieldLabel} size="xs" variant="muted">
                    Profile
                  </Text>
                  <MultiSelect
                    leadingIcon={<User size={14} />}
                    onChange={(values) =>
                      onChange({
                        profiles:
                          values.length > 0
                            ? (values as TaskProfile[])
                            : undefined,
                      })
                    }
                    options={PROFILE_OPTIONS}
                    placeholder="Profile"
                    searchPlaceholder="Profile"
                    searchable={false}
                    showSelectAll={false}
                    values={profiles}
                  />
                </div>

                <div className={css.field}>
                  <Text className={css.fieldLabel} size="xs" variant="muted">
                    Flag
                  </Text>
                  <MultiSelect
                    leadingIcon={<FlagIcon size={14} />}
                    onChange={(values) => {
                      const nums = values
                        .map((v) => Number.parseInt(v, 10))
                        .filter(
                          (n): n is TaskFlag => n === 0 || n === 5 || n === 10
                        );
                      onChange({
                        flags: nums.length > 0 ? nums : undefined,
                      });
                    }}
                    options={FLAG_OPTIONS}
                    placeholder="Flag"
                    searchPlaceholder="Flag"
                    searchable={false}
                    showSelectAll={false}
                    values={flags.map((f) => String(f))}
                  />
                </div>

                <div className={css.field}>
                  <Text className={css.fieldLabel} size="xs" variant="muted">
                    Itinerary
                  </Text>
                  <MultiSelect
                    leadingIcon={<LayoutDashboard size={14} />}
                    onChange={(values) =>
                      onChange({
                        itineraryIds: values.length > 0 ? values : undefined,
                      })
                    }
                    options={itineraryOptions}
                    placeholder={
                      itinerariesQuery.isLoading
                        ? 'Loading itineraries…'
                        : 'Itinerary'
                    }
                    searchPlaceholder="Search itineraries"
                    searchable
                    showSelectAll={false}
                    values={itineraryIds}
                  />
                </div>

                <div className={css.field}>
                  <Text className={css.fieldLabel} size="xs" variant="muted">
                    Collection
                  </Text>
                  <MultiSelect
                    leadingIcon={<LayoutTemplate size={14} />}
                    onChange={(values) =>
                      onChange({
                        collectionIds: values.length > 0 ? values : undefined,
                      })
                    }
                    options={collectionOptions}
                    placeholder={
                      collectionsQuery.isLoading
                        ? 'Loading collections…'
                        : 'Collection'
                    }
                    searchPlaceholder="Search collections"
                    searchable
                    showSelectAll={false}
                    values={collectionIds}
                  />
                </div>
              </div>
              <div className={css.footer}>
                <Button
                  onClick={() => setOpen(false)}
                  size="sm"
                  variant="primary"
                >
                  Done
                </Button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};
